/* ==========================================================================
   CLARITY HEALTH CO — BUY component  (Phase 5)
   Drives one product's purchase panel: variant selects, real Appstle selling
   plans (one-time / subscribe), quantity, and add-to-cart. Submission goes
   through a genuine Shopify product form (so the selected selling_plan + any
   app line-item data are included) intercepted for AJAX add into the shared
   Clarity cart drawer. Prices are pre-formatted by Liquid so no JS money
   formatting is required.

   If the product's own Appstle widget injects into this form (which Appstle
   only does on product-template pages), the theme-rendered plan selector is
   hidden and Appstle drives the selling_plan instead.
   ========================================================================== */
(function () {
  class ClarityBuy extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.clarity-buy__form');
      const dataEl = this.querySelector('[data-clarity-buy-data]');
      try { this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] }; } catch (e) { this.data = { variants: [] }; }
      this.variants = this.data.variants || [];
      this.selected = this.variants.find((v) => v.available) || this.variants[0] || null;
      this.qty = 1;

      this.idInput = this.querySelector('[data-clarity-id]');
      this.qtyInput = this.querySelector('[data-clarity-qty-input]');

      // Variant option selects
      this.optionSelects = Array.from(this.querySelectorAll('[data-clarity-option]'));
      this.optionSelects.forEach((s) =>
        s.addEventListener('change', () => { this.matchVariant(); this.clearError(); this.render(); })
      );

      // Plan radios (one-time / subscribe) → hidden selling_plan input
      this.sellingPlanInput = this.querySelector('[data-clarity-selling-plan]');
      this.planRadios = Array.from(this.querySelectorAll('[data-clarity-plan-radio]'));
      this.planRadios.forEach((r) => r.addEventListener('change', () => this.onPlanChange()));
      this.syncSellingPlan();

      // Quantity steppers
      this.querySelectorAll('[data-clarity-qty]').forEach((b) =>
        b.addEventListener('click', () => {
          this.qty = Math.max(1, Math.min(99, this.qty + Number(b.dataset.clarityQty)));
          this.render();
        })
      );

      // Inline add-to-cart error (e.g. a sold-out subscription plan)
      this.errorEl = this.querySelector('[data-clarity-buy-error]');
      this._onCartError = (e) => this.showError(e.detail);
      document.addEventListener('cart:error', this._onCartError);

      // Intercept form submit → AJAX add into the Clarity drawer
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      // Defer to Appstle's own widget if it injects (product-template pages only)
      this.watchForAppstle();

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:error', this._onCartError);
      if (this._appstleObs) this._appstleObs.disconnect();
    }

    /* ----- variant / plan state ----- */
    matchVariant() {
      if (!this.optionSelects.length) return;
      const chosen = this.optionSelects.map((s) => s.value);
      const m = this.variants.find(
        (v) => (v.options || []).length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
      if (m) this.selected = m;
    }

    onPlanChange() {
      this.planRadios.forEach((r) => {
        const label = r.closest('[data-clarity-plan-option]');
        if (label) label.classList.toggle('is-selected', r.checked);
      });
      this.syncSellingPlan();
      this.clearError();
      this.render();
    }

    showError(detail) {
      if (!this.errorEl) return;
      this.errorEl.textContent = (detail && (detail.description || detail.message)) || 'Sorry — this could not be added to the cart.';
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    }

    get selectedPlan() {
      const r = this.planRadios.find((x) => x.checked);
      return r ? r.value : '';
    }

    syncSellingPlan() {
      if (this.sellingPlanInput && !this.sellingPlanInput.disabled) this.sellingPlanInput.value = this.selectedPlan;
    }

    // Make sure no FOREIGN selling_plan field can ever be submitted. When the PDP
    // is loaded with ?selling_plan= in the URL, Appstle injects its own hidden
    // name="selling_plan" field (holding the plan id) into this form; if it is
    // present at add-to-cart it overrides the theme's own empty one, so a One-Time
    // purchase is charged as the subscription. A field with no name="selling_plan"
    // cannot carry a plan into /cart/add, so the theme's selector stays the single
    // source of truth. Ours is left untouched.
    neutralizeForeignPlans() {
      if (!this.form) return;
      this.form.querySelectorAll('[name="selling_plan"]').forEach((inp) => {
        if (inp !== this.sellingPlanInput) {
          inp.disabled = true;
          inp.removeAttribute('name');
          inp.setAttribute('data-clarity-neutralized', 'selling_plan');
        }
      });
    }

    priceForPlan(v, planId) {
      if (v && planId && v.plans && v.plans[planId]) return v.plans[planId];
      return v ? v.price : '';
    }

    /* ----- render ----- */
    render() {
      const v = this.selected;
      if (!v) return;

      // Variant id + quantity into the form
      if (this.idInput) this.idInput.value = v.id;
      if (this.qtyInput) this.qtyInput.value = this.qty;

      // Per-plan prices on the option cards
      this.querySelectorAll('[data-clarity-plan-price]').forEach((el) => {
        const planId = el.getAttribute('data-clarity-plan-price');
        el.textContent = this.priceForPlan(v, planId);
      });

      // Main price block reflects the chosen option (one-time or plan)
      const now = this.priceForPlan(v, this.selectedPlan);
      this.querySelectorAll('[data-clarity-price-block] [data-clarity-price-now]').forEach((el) => { el.textContent = now; });
      // Compare-at (only meaningful for one-time)
      this.querySelectorAll('[data-clarity-price-block]').forEach((block) => {
        const was = block.querySelector('[data-clarity-price-was]');
        if (was) {
          if (!this.selectedPlan && v.compare) { was.textContent = v.compare; was.hidden = false; block.querySelector('.c-price')?.classList.add('c-price--sale'); }
          else { was.hidden = true; block.querySelector('.c-price')?.classList.remove('c-price--sale'); }
        }
      });

      // Submit button availability
      const btn = this.querySelector('[data-clarity-submit]');
      const txt = this.querySelector('[data-clarity-submit-text]');
      if (btn) {
        if (v.available) {
          btn.disabled = false;
          if (txt && btn.dataset.label) txt.textContent = btn.dataset.label;
        } else {
          btn.disabled = true;
          if (txt) txt.textContent = 'Sold out';
        }
      }

      // Dispatch a change so Appstle (if present) can react to variant changes
      if (this.idInput) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ----- add to cart ----- */
    onSubmit(e) {
      e.preventDefault();
      const btn = this.querySelector('[data-clarity-submit]');
      if (btn && btn.disabled) return;
      // Standalone: make sure only our selling_plan is submitted (any Appstle
      // field is neutralised).
      if (this.hasAttribute('data-clarity-standalone')) {
        this.neutralizeForeignPlans();
      }
      this.syncSellingPlan();
      this.clearError();
      if (btn) btn.classList.add('is-loading');

      const done = () => { if (btn) btn.classList.remove('is-loading'); };

      if (window.ClarityCart && typeof window.ClarityCart.addFromForm === 'function') {
        window.ClarityCart.addFromForm(this.form).then((ok) => { if (ok) this.clearError(); }).finally(done);
      } else {
        // Fallback: post directly, then ask the drawer to refresh/open
        const body = new FormData(this.form);
        fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`.replace('//', '/'), {
          method: 'POST', headers: { Accept: 'application/json' }, body,
        })
          .then((r) => (r.ok ? r.json() : r.json().then((err) => Promise.reject(err))))
          .then(() => { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } })); })
          .catch((err) => { document.dispatchEvent(new CustomEvent('cart:error', { detail: err })); })
          .finally(done);
      }
    }

    /* ----- Appstle coexistence ----- */
    watchForAppstle() {
      if (!this.form) return;
      const standalone = this.hasAttribute('data-clarity-standalone');
      const mine = this.querySelector('[data-clarity-plans]');
      if (!standalone && !mine) return;

      // Standalone (product page): keep the theme's reused selector as the single
      // source of truth — hide Appstle's injected widget and disable its
      // selling_plan fields so only ours is submitted.
      const suppress = () => {
        this.form.querySelectorAll('[id^="appstle_subscription_widget"]').forEach((w) => {
          if (mine && mine.contains(w)) return;
          w.style.display = 'none';
          w.setAttribute('hidden', '');
        });
        this.neutralizeForeignPlans();
        this.syncSellingPlan();
      };

      // Non-standalone (homepage): defer to Appstle's real widget if it injects.
      const defer = () => {
        const injected = this.form.querySelector('[id^="appstle_subscription_widget"]');
        if (injected && (!mine || !mine.contains(injected))) {
          if (mine) mine.hidden = true;
          if (this.sellingPlanInput) this.sellingPlanInput.disabled = true;
          return true;
        }
        return false;
      };

      if (standalone) {
        suppress();
        // Watch for the life of the component — Appstle can inject its widget (and
        // a selling_plan field) well after load, past any fixed timeout, which is
        // what let the plan leak into one-time adds. Also watch `name` so a field
        // whose name is (re)added later is caught too.
        this._appstleObs = new MutationObserver(suppress);
        this._appstleObs.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['name'] });
      } else {
        if (defer()) return;
        const obs = new MutationObserver(() => { if (defer()) obs.disconnect(); });
        obs.observe(this.form, { childList: true, subtree: true });
        setTimeout(() => obs.disconnect(), 6000);
      }
    }
  }

  if (!customElements.get('clarity-buy')) customElements.define('clarity-buy', ClarityBuy);
})();
